import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Form,
  InputGroup,
  Badge,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaSearch } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  listarCartoesCreditos,
  excluirCartaoCredito,
} from '~/Services/Service';
import ModalCartao from './components/ModalCartao';

const renderStatus = (status) => {
  if (status === 'I') return <Badge variant="secondary">INATIVO</Badge>;
  return <Badge variant="success">ATIVO</Badge>;
};

const renderTipo = (tipo) => (tipo === 'P' ? 'PARTICULAR' : 'EMPRESA');

const renderLimite = (val) =>
  val != null
    ? `R$ ${Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
    : '—';

const COLUNAS = [
  { title: 'Número', key: 'numero_cartao', ordenacao: true },
  { title: 'Bandeira', key: 'descricao_bandeira', ordenacao: true },
  { title: 'Validade', key: 'validade', ordenacao: true, align: 'center' },
  { title: 'Nome', key: 'nome', ordenacao: true },
  {
    title: 'Dia Vcto',
    key: 'dia_vencimento',
    ordenacao: true,
    align: 'center',
  },
  {
    title: 'Tipo',
    key: 'tipo',
    ordenacao: true,
    render: (v) => renderTipo(v),
  },
  { title: 'GE', key: 'apelido_grupo_empresa', ordenacao: true },
  {
    title: 'Status',
    key: 'status',
    ordenacao: true,
    align: 'center',
    render: (v) => renderStatus(v),
  },
  {
    title: 'Limite Arred.',
    key: 'limite_arredondamento',
    ordenacao: true,
    align: 'right',
    render: (v) => renderLimite(v),
  },
];

const CartaoCredito = () => {
  const [cartoes, setCartoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [cartaoSelecionado, setCartaoSelecionado] = useState(null);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });

  useEffect(() => {
    carregarCartoes();
  }, []);

  const carregarCartoes = async () => {
    setLoading(true);
    try {
      const resp = await listarCartoesCreditos();
      setCartoes(resp || []);
    } catch (err) {
      toast.error(extrairMensagemErro(err, 'Erro ao carregar cartões.'));
    } finally {
      setLoading(false);
    }
  };

  // W-0527 (4ª passada): null guards — após LEFT JOIN no BE, apelido_grupo_empresa
  // pode vir '' ou null em cartões sem GE. Mesma lógica para os demais campos.
  const cartoesFiltrados = cartoes.filter((c) => {
    if (!filtro) return true;
    const q = filtro.toLowerCase();
    const numero = (c.numero_cartao || '').toLowerCase();
    const nome = (c.nome || '').toLowerCase();
    const apelidoGE = (c.apelido_grupo_empresa || '').toLowerCase();
    return numero.includes(q) || nome.includes(q) || apelidoGE.includes(q);
  });

  const handleNovo = () => {
    setCartaoSelecionado(null);
    setShowModal(true);
  };

  const handleEditar = (item) => {
    setCartaoSelecionado(item);
    setShowModal(true);
  };

  const handleExcluir = (item) => {
    setConfirmacao({ show: true, item });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    if (!confirmado) return;
    try {
      await excluirCartaoCredito(item.codigo);
      toast.success('Cartão excluído com sucesso.');
      carregarCartoes();
    } catch (err) {
      toast.error(extrairMensagemErro(err, 'Erro ao excluir cartão.'));
    }
  };

  const handleSaved = () => {
    setShowModal(false);
    carregarCartoes();
  };

  const acoes = [
    { label: 'Editar', onClick: handleEditar },
    { label: 'Excluir', onClick: handleExcluir, type: 'danger' },
  ];

  return (
    <Container fluid className="p-3">
      <Header title="Cadastro de Cartão de Crédito" />

      <Card className="mt-3">
        <Card.Body>
          <Row className="align-items-center mb-3">
            <Col xs={12} md={6} className="mb-2 mb-md-0">
              <InputGroup>
                <InputGroup.Prepend>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Filtrar por número, nome ou GE..."
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={6} className="text-md-right">
              <Button variant="primary" onClick={handleNovo}>
                <FaPlus className="mr-1" /> Novo Cartão
              </Button>
            </Col>
          </Row>

          {loading ? (
            <Loading />
          ) : (
            <Tabela
              colunas={COLUNAS}
              dados={cartoesFiltrados}
              action
              acoes={acoes}
            />
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <ModalCartao
          show={showModal}
          onHide={() => setShowModal(false)}
          cartao={cartaoSelecionado}
          onSaved={handleSaved}
        />
      )}

      <ModalConfirmacao
        show={confirmacao.show}
        onClose={handleConfirmarExclusao}
        title="Excluir Cartão"
        message={`Confirma excluir o cartão ${confirmacao.item ? confirmacao.item.numero_cartao : ''}?`}
        variant="danger"
      />
    </Container>
  );
};

export default CartaoCredito;
